import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload as UploadIcon, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { toast } from "sonner";

const ACCEPTED_EXTS = [".pdf", ".docx", ".xlsx"];
const MAX_BYTES = 50 * 1024 * 1024; // 50 Mo

/**
 * Inline drop zone at the bottom of the Documents page.
 *
 * Same UX intent as the dedicated /upload page but compact, kept within
 * the Documents module so the user never leaves the library to add a doc.
 * On success → toast + navigate to /analysis/<id>.
 */
export default function DocumentsDropZone({ onUploaded }) {
  const { t } = useTranslation("documents");
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ACCEPTED_EXTS.includes(ext)) {
      toast.error(t("dropzone.unsupported_ext", { ext }));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("dropzone.too_large"));
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("dropzone.upload_success", { name: file.name }));
      onUploaded?.(r.data);
      if (r.data?.id) navigate(`/analysis/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dropzone.upload_failed"));
    } finally {
      setUploading(false);
    }
  }, [t, navigate, onUploaded]);

  return (
    <div
      data-testid="documents-dropzone"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        handleFile(file);
      }}
      onClick={() => !uploading && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      className={`mt-4 rounded-2xl border-2 border-dashed transition-all cursor-pointer px-5 py-6 flex items-center justify-between gap-4 flex-wrap ${
        dragOver
          ? "border-qenva-blue bg-qenva-blue/5"
          : "border-zinc-200 bg-white hover:border-qenva-blue/50 hover:bg-qenva-off"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTS.join(",")}
        className="sr-only"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="documents-dropzone-input"
      />
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-qenva-blue-soft grid place-items-center text-qenva-blue shrink-0">
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <UploadIcon size={18} strokeWidth={1.75} />}
        </div>
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-qenva-navy truncate">
            {uploading ? t("dropzone.uploading") : t("dropzone.title")}
          </p>
          <p className="text-[12px] text-zinc-500 truncate">{t("dropzone.subtitle")}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        {["PDF", "DOCX", "XLSX"].map((ext) => (
          <span
            key={ext}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600 text-[10px] font-semibold tracking-wider"
          >
            {ext}
          </span>
        ))}
        <span className="text-[11px] text-zinc-400">{t("dropzone.max")}</span>
      </div>
    </div>
  );
}
